"use client";

import useRecaptcha from "@/hooks/useRecaptcha";
import { useTimer } from "@/hooks/useTimer";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { profileService } from "@/services/accountServices/profileServices";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";

export default function ForgotPassword({ onBackToLogin }) {
  const t = useTranslationDetector();
  const locale = useLocale();
  const { getCaptchaToken } = useRecaptcha();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(null);
  const [resetToken, setResetToken] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);
  const [isSubmittingStep2, setIsSubmittingStep2] = useState(false);
  const [isSubmittingStep3, setIsSubmittingStep3] = useState(false);
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const timer = useTimer({
    initialTime: 180,
    direction: "down",
  });

  const clearInlineError = () => setInlineError("");

  const setServiceError = (payload) => {
    if (!payload) return;
    const type = payload?.Type;
    const name = payload?.Name;
    setInlineError(type && name ? t(`${type}.${name}`) : t("common.error"));
  };

  // Step 1: Email schema
  const emailSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("common.required"))
          .email(t("validations.email")),
      }),
    [t],
  );

  // Step 2: OTP schema
  const otpSchema = useMemo(
    () =>
      z.object({
        otpCode: z
          .string()
          .min(1, t("common.required"))
          .length(6, t("common.otpLength"))
          .regex(/^[A-Za-z0-9]+$/, t("common.otpFormat")),
      }),
    [t],
  );

  // Step 3: Password reset schema
  const passwordSchema = useMemo(
    () =>
      z
        .object({
          NewPassword: z
            .string()
            .min(1, t("common.required"))
            .min(6, t("validations.passwordmin"))
            .max(50, t("validations.passwordmax")),
          ConfirmNewPassword: z.string().min(1, t("common.required")),
        })
        .refine((data) => data.NewPassword === data.ConfirmNewPassword, {
          message: t("validations.passwordsNotMatch"),
          path: ["ConfirmNewPassword"],
        }),
    [t],
  );

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: { otpCode: "" },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: { NewPassword: "", ConfirmNewPassword: "" },
  });

  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    reset: resetEmailForm,
  } = emailForm;

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    formState: { errors: otpErrors },
    reset: resetOtpForm,
  } = otpForm;

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
  } = passwordForm;

  useEffect(() => {
    if (step === 2) timer.restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    setInlineError("");
  }, [step]);

  const onEmailSubmit = async (data) => {
    clearInlineError();
    setIsSubmittingStep1(true);
    try {
      const RecaptchaToken = await getCaptchaToken("forgotpassword");
      const response = await profileService.sendForgotPasswordOTP({
        Email: data.email,
        LanguageCode: locale.toUpperCase(),
        RecaptchaToken,
        IsWebPlatform: true,
      });

      if (!response?.Message) {
        setFormData(data);
        setStep(2);
        timer.restart();
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsSubmittingStep1(false);
    }
  };

  const onOtpSubmit = async (data) => {
    if (timer.timeLeft <= 0) return;

    clearInlineError();
    setIsSubmittingStep2(true);
    try {
      const RecaptchaToken = await getCaptchaToken("forgotpassword");
      const response = await profileService.verifyForgotPasswordOTP({
        Email: formData?.email,
        OtpCode: data.otpCode,
        RecaptchaToken: RecaptchaToken,
        IsWebPlatform: true,
      });

      if (!response?.Message) {
        // API may return ResetToken in response, or OTP itself serves as token
        const token =
          response?.ResetToken || response?.resetToken || data.otpCode;
        setResetToken(token);
        setStep(3);
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsSubmittingStep2(false);
    }
  };

  const handleResendOTP = async () => {
    if (timer.timeLeft > 0) return;
    if (!formData?.email) return;

    clearInlineError();
    setIsResendingOTP(true);
    try {
      const RecaptchaToken = await getCaptchaToken("forgotpassword");
      const response = await profileService.sendForgotPasswordOTP({
        Email: formData.email,
        LanguageCode: locale.toUpperCase(),
        RecaptchaToken,
        IsWebPlatform: true,
      });

      if (!response?.Message) {
        timer.restart();
        resetOtpForm();
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsResendingOTP(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    clearInlineError();
    setIsSubmittingStep3(true);
    try {
      const RecaptchaToken = await getCaptchaToken("forgotpassword");
      const response = await profileService.resetPassword({
        ResetToken: resetToken,
        NewPassword: data.NewPassword,
        ConfirmNewPassword: data.ConfirmNewPassword,
        RecaptchaToken,
        IsWebPlatform: true,
      });

      if (!response?.Message || response?.IsSuccess) {
        setSuccessMessage(t("common.passwordResetSuccess"));

        setTimeout(() => {
          onBackToLogin();
        }, 3000);

        // setStep(1);
        // setFormData(null);
        // setResetToken("");
        // resetEmailForm();
        // resetOtpForm();
        // passwordForm.reset();
        // timer.reset();
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsSubmittingStep3(false);
    }
  };

  const InlineError = inlineError ? (
    <div className="text-danger border-danger/20 bg-danger/5 rounded-lg border px-4 py-3 text-sm">
      {inlineError}
    </div>
  ) : null;

  const stepTitles = {
    1: t("common.step1Title"),
    2: t("common.step2Title"),
    3: t("common.step3Title"),
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Step indicator */}
      <div className="text-xl font-semibold">
        {step === 1 && stepTitles[1]}
        {step === 2 && stepTitles[2]}
        {step === 3 && stepTitles[3]}
      </div>

      {successMessage && (
        <div className="text-success bg-success/10 border-success/20 rounded-lg border px-4 py-3 text-sm">
          {successMessage}
        </div>
      )}

      {/* Step 1: Email */}
      {step === 1 && (
        <div className="flex flex-col gap-7">
          <p className="text-foreground-muted">
            {t("common.forgotPasswordDescription")}
          </p>

          <form
            onSubmit={handleEmailSubmit(onEmailSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <Controller
                name="email"
                control={emailControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t("common.email")}
                    placeholder={t("common.emailPlaceholder")}
                    error={emailErrors.email?.message}
                    type="email"
                    required
                  />
                )}
              />
            </div>

            {InlineError}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={isSubmittingStep1}
            >
              {t("common.send")}
            </Button>
          </form>
        </div>
      )}

      {/* Step 2: OTP */}
      {step === 2 && (
        <div className="flex flex-col gap-7">
          <p className="text-foreground-muted">{t("common.otpDescription")}</p>

          <form
            onSubmit={handleOtpSubmit(onOtpSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <Controller
                name="otpCode"
                control={otpControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t("common.otpCode")}
                    placeholder={t("common.otpCodePlaceholder")}
                    error={otpErrors.otpCode?.message}
                    required
                    maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .slice(0, 6);
                      field.onChange(value);
                    }}
                  />
                )}
              />

              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-sm">
                  <span>{t("common.timeRemaining")}</span>
                  <span className="text-danger">{timer.formatTime()}</span>
                </div>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={timer.timeLeft > 0 || isResendingOTP}
                  className="text-primary-strong inline-flex cursor-pointer items-center gap-2 text-sm disabled:opacity-50"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["arrows-rotate-reverse"]}
                  />
                  <span>
                    {isResendingOTP
                      ? t("common.sending")
                      : t("common.resendCode")}
                  </span>
                </button>
              </div>
            </div>

            {InlineError}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={isSubmittingStep2}
              disabled={timer.timeLeft <= 0}
            >
              {isSubmittingStep2 ? t("common.verifying") : t("common.verify")}
            </Button>
          </form>
        </div>
      )}

      {/* Step 3: New Password Form */}
      {step === 3 && (
        <div className="flex flex-col gap-7">
          <p className="text-foreground-muted">
            {t("common.passwordFormDescription") ||
              t("common.changePasswordDescription")}
          </p>

          <form
            onSubmit={handlePasswordSubmit(onPasswordSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <Controller
                name="NewPassword"
                control={passwordControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={
                      t("common.newPassword") || t("registermodal.password")
                    }
                    placeholder={t("common.newPasswordPlaceholder")}
                    error={passwordErrors.NewPassword?.message}
                    type="password"
                    required
                  />
                )}
              />

              <Controller
                name="ConfirmNewPassword"
                control={passwordControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={
                      t("common.confirmNewPassword") ||
                      t("registermodal.confirmPassword")
                    }
                    placeholder={t("common.confirmPasswordPlaceholder")}
                    error={passwordErrors.ConfirmNewPassword?.message}
                    type="password"
                    required
                  />
                )}
              />
            </div>

            {InlineError}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={isSubmittingStep3}
            >
              {t("common.reset") || t("common.save")}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
